import React, { useEffect, useState } from 'react';
import { Table, Button, Select, Input, DatePicker, Form, message, Row, Col, Checkbox, Modal, Popconfirm, Upload, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import CurrentAssignments from '../components/CurrentAssignments';
import {
  getDepartments,
  getSubcontractors,
  getClients,
  getClientDepartments,
  getEmployeeAssignments,
  createEmployeeAssignments,
  getSubcontractorEmployees,
  getEmployee,
  getEmployees,
  deleteEmployeeAssignment
} from '../api';
import moment from 'moment';
import { UploadOutlined, DownloadOutlined, CalendarOutlined } from '@ant-design/icons';
import api from '../api';

const { Option } = Select;
const { MonthPicker } = DatePicker;

const EmployeeAssignments = () => {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');
  // Filters
  const [departmentId, setDepartmentId] = useState();
  const [subcontractorId, setSubcontractorId] = useState();
  const [employeeType, setEmployeeType] = useState('internal');
  const [searchName, setSearchName] = useState('');
  const [clientId, setClientId] = useState();
  const [clientDepartmentId, setClientDepartmentId] = useState();
  const [month, setMonth] = useState();
  // Data
  const [departments, setDepartments] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientDepartments, setClientDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [employeeAssignments, setEmployeeAssignments] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Assignment list state
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentFilters, setAssignmentFilters] = useState({});

  // Form and validation
  const [form] = Form.useForm();
  const [clientDeptTouched, setClientDeptTouched] = useState(false);

  // Bulk import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  
  // Date range assignment state
  const [dateRangeModalVisible, setDateRangeModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [dateRangeClientId, setDateRangeClientId] = useState(null);
  const [dateRangeDeptId, setDateRangeDeptId] = useState(null);

  // Fetch filter data
  useEffect(() => {
    getDepartments().then(res => { if (res.success) setDepartments(res.data); });
    getSubcontractors().then(res => { if (res.success) setSubcontractors(res.data); });
    getClients().then(res => { if (res.success) setClients(res.data); });
  }, []);

  useEffect(() => {
    if (clientId) {
      getClientDepartments(clientId).then(res => {
        if (res.success) {
          setClientDepartments(res.data);
          if (res.data.length === 0) setClientDepartmentId(null);
        }
      });
    } else {
      setClientDepartments([]);
      setClientDepartmentId(null);
    }
  }, [clientId]);

  // Fetch employees based on filters
  useEffect(() => {
    setLoading(true);
    if (employeeType === 'internal') {
      getEmployees().then(res => {
        console.log(res)
        let data = res.success ? res.data : [];
        if (departmentId) data = data.filter(e => e.department_id === departmentId);
        if (searchName) data = data.filter(e => e.name.toLowerCase().includes(searchName.toLowerCase()));
        setEmployees(data);
        setLoading(false);
      });
    } else {
      getSubcontractorEmployees(subcontractorId).then(res => {
        let data = res.success ? res.data : [];
        if (searchName) data = data.filter(e => e.name.toLowerCase().includes(searchName.toLowerCase()));
        setEmployees(data);
        setLoading(false);
      });
    }
  }, [employeeType, departmentId, subcontractorId, searchName]);

  // Fetch assignments
  const fetchAssignments = async (filters = {}) => {
    setAssignmentsLoading(true);
    const res = await getEmployeeAssignments(filters);
    setAssignmentsLoading(false);
    if (res.success) setAssignments(res.data);
    else setAssignments([]);
  };

  // Handle unassigning an employee from an assignment
  const handleUnassign = async (assignmentId, employeeId) => {
    try {
      const response = await deleteEmployeeAssignment(assignmentId);
      if (response.success) {
        message.success('Assignment removed successfully');
        // Update the UI by removing the unassigned assignment
        setEmployeeAssignments(prev => ({
          ...prev,
          [employeeId]: prev[employeeId].filter(a => a.id !== assignmentId)
        }));
      } else {
        message.error(response.message || 'Failed to remove assignment');
      }
    } catch (error) {
      console.error('Error unassigning employee:', error);
      message.error('An error occurred while removing the assignment');
    }
  };

  // Fetch existing assignments for employees in the selected month
  const fetchEmployeeAssignments = async (employeeIds, month) => {
    if (!month || employeeIds.length === 0) {
      setEmployeeAssignments({});
      return;
    }
    
    setLoadingAssignments(true);
    const startDate = month.clone().startOf('month').format('YYYY-MM-DD');
    const endDate = month.clone().endOf('month').format('YYYY-MM-DD');
    
    try {
      const res = await getEmployeeAssignments({
        start_date: startDate,
        end_date: endDate
      });
      
      if (res.success) {
        const assignmentsMap = {};
        res.data.forEach(assignment => {
          if (employeeIds.includes(assignment.employee_id)) {
            if (!assignmentsMap[assignment.employee_id]) {
              assignmentsMap[assignment.employee_id] = [];
            }
            assignmentsMap[assignment.employee_id].push(assignment);
          }
        });
        setEmployeeAssignments(assignmentsMap);
      }
    } catch (error) {
      console.error('Error fetching employee assignments:', error);
      setEmployeeAssignments({});
    } finally {
      setLoadingAssignments(false);
    }
  };

  // Fetch assignments when filters change
  useEffect(() => {
    fetchAssignments(assignmentFilters);
  }, [JSON.stringify(assignmentFilters)]);

  // Fetch employee assignments when employees or month changes
  useEffect(() => {
    if (employees.length > 0 && month) {
      const employeeIds = employees.map(emp => emp.id);
      fetchEmployeeAssignments(employeeIds, month);
    }
  }, [employees, month]);

  // Table columns
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name',
      render: (name, record) => {
        const assignments = employeeAssignments[record.id] || [];
        const hasAssignments = assignments.length > 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{name}</span>
            {hasAssignments && (
              <span style={{
                backgroundColor: '#1890ff',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: 'bold'
              }}>
                {assignments.length} assignment{assignments.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        );
      }
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Department', dataIndex: 'department', key: 'department', render: (d, r) => r.department ? r.department.name : '' },
    { title: 'Subcontractor', dataIndex: 'subcontractor_id', key: 'subcontractor_id', render: (v, r) => r.subcontractor ? r.subcontractor.name : '' },
    { 
      title: 'Current Assignments', 
      dataIndex: 'current_assignments', 
      key: 'current_assignments', 
      width: 300,
      render: (_, record) => {
        if (loadingAssignments) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>Loading...</span>;
        }
        const assignments = employeeAssignments[record.id] || [];
        if (assignments.length === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>No assignments</span>;
        }
        return (
          <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
            {assignments.map((assignment, index) => (
              <div key={index} style={{ 
                marginBottom: '2px', 
                padding: '4px 8px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <span style={{ fontWeight: 'bold' }}>
                    {assignment.client ? assignment.client.name : 'Unknown Client'}
                  </span>
                  <span style={{ color: '#666', marginLeft: '8px' }}>
                    {moment(assignment.start_date).format('MMM DD')} - {moment(assignment.end_date).format('MMM DD')}
                  </span>
                </div>
                <Popconfirm
                  title="Are you sure you want to unassign this employee?"
                  onConfirm={(e) => {
                    e.stopPropagation();
                    handleUnassign(assignment.id, record.id);
                  }}
                  onCancel={e => e.stopPropagation()}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    type="text" 
                    size="small" 
                    danger
                    onClick={e => e.stopPropagation()}
                    style={{ padding: '0 4px', height: 'auto' }}
                  >
                    Unassign
                  </Button>
                </Popconfirm>
              </div>
            ))}
          </div>
        );
      }
    },
  ];

  // Assignment table columns
  const assignmentColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Employee', dataIndex: 'employee', key: 'employee', render: (e, r) => r.employee ? r.employee.name : '' },
    { title: 'Type', dataIndex: 'employee_type', key: 'employee_type' },
    { title: 'Client', dataIndex: 'client', key: 'client', render: (c, r) => r.client ? r.client.name : '' },
    { title: 'Department', dataIndex: 'clientDepartment', key: 'clientDepartment', render: (d, r) => r.clientDepartment ? r.clientDepartment.name : '' },
    { title: 'Start', dataIndex: 'start_date', key: 'start_date' },
    { title: 'End', dataIndex: 'end_date', key: 'end_date' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title="Unassign this employee?" onConfirm={async () => {
          await deleteEmployeeAssignment(record.id);
          fetchAssignments(assignmentFilters);
        }} okText="Yes" cancelText="No">
          <Button size="small" danger>Unassign</Button>
        </Popconfirm>
      ),
    },
  ];

  // Assignment filter UI
  const handleAssignmentFilterChange = (key, value) => {
    setAssignmentFilters(prev => ({ ...prev, [key]: value }));
  };

  // Date range assignment handler
  const handleDateRangeAssign = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      message.error('Please select both start and end dates');
      return;
    }
    if (!dateRangeClientId) {
      message.error('Please select a client');
      return;
    }
    if (clientDepartments.length > 0 && !dateRangeDeptId) {
      message.error('Please select a client department');
      return;
    }

    setAssigning(true);
    try {
      const payload = {
        employee_ids: selectedRowKeys,
        employee_type: employeeType,
        client_id: dateRangeClientId,
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
      };
      
      if (clientDepartments.length > 0) {
        payload.client_department_id = dateRangeDeptId;
      } else {
        payload.department_id = departmentId;
      }

      const res = await createEmployeeAssignments(payload);
      if (res.success) {
        message.success('Date range assigned successfully');
        setDateRangeModalVisible(false);
        setSelectedRowKeys([]);
        // Refresh employee assignments to show the new assignments
        const employeeIds = employees.map(emp => emp.id);
        fetchEmployeeAssignments(employeeIds, month);
      } else {
        message.error(res.message || 'Failed to assign date range');
      }
    } catch (error) {
      console.error('Error assigning date range:', error);
      message.error('Error assigning date range');
    } finally {
      setAssigning(false);
    }
  };

  // Bulk assign handler
  const handleAssign = async () => {
    if (!clientId) {
      message.error('Please select a client.');
      return;
    }
    if (!month) {
      message.error('Please select a month.');
      return;
    }
    if (selectedRowKeys.length === 0) {
      message.error('Please select at least one employee.');
      return;
    }
    console.log(clientDepartments)
    if (clientDepartments.length > 0 && !clientDepartmentId) {
      setClientDeptTouched(true);
      message.error('Please select a client department.');
      return;
    }
    alert(month)
    const start_date = month.clone().startOf('month');
    const end_date = month.clone().endOf('month');
    // if (start_date.isBefore(moment(), 'day')) {
    //   message.error('Start date cannot be in the past.');
    //   return;
    // }
    // if (end_date.isBefore(start_date, 'day')) {
    //   message.error('End date cannot be before start date.');
    //   return;
    // }
    setAssigning(true);
    const payload = {
      employee_ids: selectedRowKeys,
      employee_type: employeeType,
      client_id: clientId,
      start_date: start_date.format('YYYY-MM-DD'),
      end_date: end_date.format('YYYY-MM-DD'),
    };
    if (clientDepartments.length > 0 && clientDepartmentId) {
      payload.client_department_id = clientDepartmentId;
    } else {
      payload.department_id = departmentId;
    }
    const res = await createEmployeeAssignments(payload);
    setAssigning(false);
    if (res.success) {
      message.success('Employees assigned successfully');
      setSelectedRowKeys([]);
    } else {
      message.error(res.message || 'Assignment failed');
    }
  };

  // Bulk import handler
  const handleImport = async (file) => {
    if (!clientId) {
      message.error('Please select a client.');
      return false;
    }
    if (clientDepartments.length > 0 && !clientDepartmentId) {
      message.error('Please select a client department.');
      return false;
    }
    if (!month) {
      message.error('Please select a month.');
      return false;
    }
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    formData.append('month', month.format('YYYY-MM'));
    if (clientDepartmentId) formData.append('client_department_id', clientDepartmentId);

    try {
      const res = await api.post('/employee-assignments/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(res.data);
      setImportModalVisible(true);
      console.log(res)
      if (res.success) {
        message.success('Import completed successfully');
        fetchAssignments(assignmentFilters);
        // Refresh employee assignments to show the new assignments
        const employeeIds = employees.map(emp => emp.id);
        fetchEmployeeAssignments(employeeIds, month);
      } else {
        message.error(res.message || 'Import failed. Please check the file format and try again.');
      }
    } catch (error) {
      console.error('Import error:', error);
      message.error(error.message || 'An error occurred during import');
    } finally {
      setImporting(false);
    }
    
    return false;
  };

  // Sample CSV content
  const sampleCsv = 'emp_code,name\nDT0001,Reghuraman K\nDT0002,Cheeroth Sivadasan\n';
  const downloadSampleCsv = () => {
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_assignment_sample.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    console.log('Month state changed:', month);
  }, [month]);

  // Date Range Assignment Modal
  const DateRangeModal = () => (
    <Modal
      title="Assign Date Range"
      open={dateRangeModalVisible}
      onCancel={() => setDateRangeModalVisible(false)}
      onOk={handleDateRangeAssign}
      confirmLoading={assigning}
      okText="Assign Date Range"
    >
      <Form layout="vertical">
        <Form.Item label="Date Range" required>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={setDateRange}
            disabledDate={current => current && current < moment().startOf('day')}
          />
        </Form.Item>
        <Form.Item label="Client" required>
          <Select
            value={dateRangeClientId}
            onChange={setDateRangeClientId}
            placeholder="Select client"
          >
            {clients.map(client => (
              <Option key={client.id} value={client.id}>
                {client.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {clientDepartments.length > 0 && (
          <Form.Item label="Client Department" required>
            <Select
              value={dateRangeDeptId}
              onChange={setDateRangeDeptId}
              placeholder="Select department"
              loading={clientDepartments.length === 0}
            >
              {clientDepartments.map(dept => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );

  if (view === 'current') {
    return (
      <div>
        <CurrentAssignments
          clients={clients}
          clientDepartments={clientDepartments}
          assignments={assignments}
          assignmentsLoading={assignmentsLoading}
          assignmentFilters={assignmentFilters}
          handleAssignmentFilterChange={handleAssignmentFilterChange}
          assignmentColumns={assignmentColumns}
        />
      </div>
    );
  }

  return (
    <div>
      <h2>Bulk Employee Assignment</h2>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          employeeType,
          departmentId,
          subcontractorId,
          clientId,
          clientDepartmentId,
        }}
        onValuesChange={(changed, all) => {
          if ('employeeType' in changed) setEmployeeType(changed.employeeType);
          if ('departmentId' in changed) setDepartmentId(changed.departmentId);
          if ('subcontractorId' in changed) setSubcontractorId(changed.subcontractorId);
          if ('clientId' in changed) setClientId(changed.clientId);
          if ('clientDepartmentId' in changed) setClientDepartmentId(changed.clientDepartmentId);
        }}
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Form.Item name="employeeType" label="Employee Type">
              <Select>
                <Option value="internal">Internal</Option>
                <Option value="subcontractor">Subcontractor</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="departmentId" label="Department" rules={[{ required: employeeType === 'internal', message: 'Please select a department.' }]}> 
              <Select allowClear disabled={employeeType !== 'internal'}>
                {departments.map(dep => <Option key={dep.id} value={dep.id}>{dep.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="subcontractorId" label="Subcontractor" rules={[{ required: employeeType === 'subcontractor', message: 'Please select a subcontractor.' }]}> 
              <Select allowClear disabled={employeeType !== 'subcontractor'}>
                {subcontractors.map(sub => <Option key={sub.id} value={sub.id}>{sub.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="month" label="Month" rules={[{ required: true, message: 'Please select a month.' }]}> 
              <MonthPicker style={{ width: '100%' }} value={month} onChange={setMonth} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="clientId" label="Client" rules={[{ required: true, message: 'Please select a client.' }]}> 
              <Select allowClear>
                {clients.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            {clientDepartments.length > 0 && (
              <Form.Item name="clientDepartmentId" label="Client Department" rules={[{ required: true, message: 'Please select a client department.' }]}> 
                <Select allowClear disabled={!clientId}>
                  {clientDepartments.map(dep => <Option key={dep.id} value={dep.id}>{dep.name}</Option>)}
                </Select>
              </Form.Item>
            )}
          </Col>
          <Col span={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Form.Item>
              <Input
                placeholder="Search name"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="default"
              icon={<CalendarOutlined />}
              onClick={() => setDateRangeModalVisible(true)}
              disabled={selectedRowKeys.length === 0}
              style={{ marginRight: 8 }}
            >
              Assign Date Range
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                console.log('Assigning with month:', month);
                try {
                  await form.validateFields();
                  if (selectedRowKeys.length === 0) {
                    message.error('Please select at least one employee.');
                    return;
                  }
                  console.log(month)

                  const values = form.getFieldsValue();
                  const start_date = month.clone().startOf('month');
                  const end_date = month.clone().endOf('month');
                  setAssigning(true);
                  const payload = {
                    employee_ids: selectedRowKeys,
                    employee_type: values.employeeType,
                    client_id: values.clientId,
                    start_date: start_date.format('YYYY-MM-DD'),
                    end_date: end_date.format('YYYY-MM-DD'),
                  };
                  if (clientDepartments.length > 0 && values.clientDepartmentId) {
                    payload.client_department_id = values.clientDepartmentId;
                  } else {
                    payload.department_id = values.departmentId;
                  }
                  const res = await createEmployeeAssignments(payload);
                  setAssigning(false);
                  if (res.success) {
                    message.success('Employees assigned successfully');
                    setSelectedRowKeys([]);
                    // Refresh employee assignments to show the new assignments
                    const employeeIds = employees.map(emp => emp.id);
                    fetchEmployeeAssignments(employeeIds, month);
                  } else {
                    message.error(res.message || 'Assignment failed');
                  }
                } catch (err) {
                  // Validation errors are shown inline
                }
              }}
              loading={assigning}
              disabled={selectedRowKeys.length === 0}
            >
              Assign Full Month
            </Button>
            <Upload
              accept=".csv"
              showUploadList={false}
              beforeUpload={handleImport}
              disabled={!clientId || (clientDepartments.length > 0 && !clientDepartmentId) || !month || importing}
            >
              <Button icon={<UploadOutlined />} loading={importing}>
                Import
              </Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={downloadSampleCsv}>
              Sample CSV
            </Button>
          </Col>
        </Row>
      </Form>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={employees}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        pagination={{ pageSize: 10 }}
        bordered
        style={{ marginTop: 16 }}
      />
      <Modal
        title="Import Result"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
      >
        {importResult && (
          <div>
            <Typography.Paragraph><b>Assigned:</b> {importResult.assigned?.length || 0}</Typography.Paragraph>
            <Typography.Paragraph><b>Not Found:</b> {importResult.not_found?.length || 0}</Typography.Paragraph>
            <Typography.Paragraph><b>Errors:</b> {importResult.errors?.length || 0}</Typography.Paragraph>
            {importResult.not_found?.length > 0 && (
              <div style={{ maxHeight: 120, overflow: 'auto', marginBottom: 8 }}>
                <b>Not Found Emp Codes:</b>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importResult.not_found.map((row, idx) => (
                    <li key={idx}>{row.emp_code} ({row.name})</li>
                  ))}
                </ul>
              </div>
            )}
            {importResult.errors?.length > 0 && (
              <div style={{ maxHeight: 120, overflow: 'auto' }}>
                <b>Errors:</b>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importResult.errors.map((err, idx) => (
                    <li key={idx}>{err.row?.emp_code} ({err.row?.name}): {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
      
      <DateRangeModal />
    </div>
  );
};

export default EmployeeAssignments;